import {Injectable, NgZone} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {Action, select, Store} from '@ngrx/store';
import {NotificationService} from '../service/notification.service';
import * as NotificationActions from '../../notifications/store/notification.actions';
import {
  getStockUpdateChanges,
  getStockUpdateChangesSuccess,
  setPropertyOnNotificationState,
} from './notification.actions';
import * as UserActions from '../../user-access/user-store/user.actions';
import {setGlobalModalVisibility} from '../../user-access/user-store/user.actions';
import * as SettingsActions from '../../settings/store/settings.actions';
import {catchError, concatMap, filter, map, mergeMap, switchMap, withLatestFrom} from 'rxjs/operators';
import {EMPTY, from, of} from 'rxjs';
import {
  selectIsUserLoggedIn,
  selectLoginProgressProperty,
  selectUser,
} from '../../user-access/user-store/user.selectors';
import {INotification} from '../models';
import {
  path_notification_id,
  path_notifications,
  path_operational_stores_data_auto_ordering_auto_orders_order_id,
  path_operational_stores_data_storeId_data_api_events_eventId,
} from '../../../shared/shared-services/database-paths';
import {selectSuppliersByUserSelectedStore} from '../../core/store-shared/shared.selectors';
import {StockItemUpdateObj, ApiLogObj2} from '../../../shared-utilities/models-old/datastructures';
import {selectNotificationLastDocumentId, selectSelectedNotification} from './notification.selectors';
import {FirebaseService} from '../../../shared-utilities/services-old/firebase.service';
import {FirebaseError} from '@angular/fire/app';
import {SharedModalUtils} from '../../../shared/shared-utils/shared-modal/shared-modal.utils';
import {ISharedModalBasic} from '../../../shared/shared-models/modals/shared-modal-basic';
import {IconColourEnum} from '../../../shared-modules/shared-icons/models/icons-colour.enum';
import {ModalType} from '../../global-modal-container/models/modal-enums';
import {IAutoOrderOrder} from '../../auto-ordering/models';
import {calculatePercentile} from '../../../shared/shared-utils/number/number.utils';

import {IconsEnum} from '../../../shared-modules/shared-icons/models/icons.enum';
import {
  extractStockTagRestorePayload,
  mergeTagChangesIntoStockViewChanges,
} from '../utils/notification-stock-update-view-changes.utils';
import {CollectionSharedStockService} from '../../../shared/shared-services/firebase/collection-shared-stock.service';
import {IStore} from '../../../shared/shared-models/store/store';

@Injectable()
export class NotificationEffects {

  //#region GET ALL NOTIFICATIONS LIVE CHANGES
  /**
   * ### getAllNotificationsLiveChanges$
   * Effect to handle the streaming of all live notifications from Firestore.
   *
   * This effect listens for the `NotificationActions.getAllNotificationsLiveChanges` action, calls the
   * `notificationService.getLiveNotifications` method, and dispatches success or failure actions accordingly.
   *
   * ### Trigger
   * - `NotificationActions.getAllNotificationsLiveChanges`
   *
   * ### Flow
   * 1. Triggered by the `getAllNotificationsLiveChanges` action, which provides `storeIds` and login context.
   * 2. Uses selectors to fetch the current user, login progress, and login status.
   * 3. If the user is logged in and store IDs are available, calls `getLiveNotifications`.
   * 4. On success:
   *    - Dispatches `getAllNotificationsLiveChangesSuccess` with the live notifications.
   *    - On the first snapshot while logging in, also dispatches:
   *      - `UserActions.setLoginProgress`
   *      - `SettingsActions.getAllUserStoreSettings`
   *      Later snapshots only update notifications so a stock-update notification
   *      cannot replay login and switch the selected store.
   * 5. On error:
   *    - If user is admin, logs the error to the console.
   *    - Dispatches `NotificationActions.setGlobalErrorOccurrence` with structured error metadata.
   *    - Dispatches fallback UI actions to reset notification state flags.
   *
   * ### Side Effects
   * - May update login progress and trigger user store settings load on the first snapshot while logging in.
   * - May log to the console for debugging if the user is an admin.
   *
   * ### Returns
   * - On success:
   *   - `NotificationActions.getAllNotificationsLiveChangesSuccess`
   *   - `UserActions.setLoginProgress` (first snapshot while logging in)
   *   - `SettingsActions.getAllUserStoreSettings` (first snapshot while logging in)
   * - On failure:
   *   - `NotificationActions.setGlobalErrorOccurrence`
   *   - `NotificationActions.setPropertyOnNotificationState` (for loading flags)
   */
  getAllNotificationsLiveChanges$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(NotificationActions.getAllNotificationsLiveChanges),
      withLatestFrom(
        this.store.select(selectUser),
        this.store.select(selectLoginProgressProperty),
        this.store.select(selectIsUserLoggedIn),
      ),
      filter(([_, user]) => !!user),
      switchMap(([action, user, loginSteps, isLoggedIn]) => {
        const {isLoggingIn, storeIds} = action;

        // Empty storeIds intentionally tears down any prior live subscription
        // (switchMap unsubscribes the previous inner) without starting a new one.
        if (!isLoggedIn || !storeIds?.length) {
          return EMPTY;
        }

        let hasFinishedLoginFromThisSubscription = false;

        return this.notificationService.getLiveNotifications(
          path_notifications(),
          storeIds,
        ).pipe(
          mergeMap((notifications: INotification[]) => {
            const actions: Action[] = [
              NotificationActions.getAllNotificationsLiveChangesSuccess({
                isLoggingIn,
                notifications,
                storeIds,
              }),
            ];

            // The live listener keeps emitting after login. Replaying step 5 on
            // later snapshots (for example a stock-update notification) restarts
            // settings init and can switch the selected store (FE-219).
            if (isLoggingIn === true && !hasFinishedLoginFromThisSubscription) {
              hasFinishedLoginFromThisSubscription = true;
              actions.push(
                UserActions.setLoginProgress({
                  progress: {
                    ...loginSteps,
                    currentStep: 5,
                    percentComplete: calculatePercentile(5, loginSteps.totalSteps),
                  },
                }),
                SettingsActions.getAllUserStoreSettings(),
              );
            }

            return actions;
          }),
          catchError((error) => {
            return of(
              NotificationActions.setGlobalErrorOccurrence({
                error: {
                  ...error,
                  action: JSON.stringify(action),
                  actionType: action.type,
                  originalError: error,
                  userId: user.id,
                  storeIds,
                },
              }),
              NotificationActions.setPropertyOnNotificationState({
                property: 'isUserNotificationsLoading',
                value: false,
              }),
              NotificationActions.setPropertyOnNotificationState({
                property: 'isUserScrollLoading',
                value: false,
              }),
            );
          }),
        );
      }),
    );
  });
  //#endregion

  //#region GET ALL NOTIFICATIONS PAGED
  /**
   * ### getAllNotificationsPaged$
   * Effect to handle the paginated loading of all notifications from Firestore.
   *
   * This effect listens for the `NotificationActions.getAllNotificationsPaged` action, calls the
   * `notificationService.getLatestNotificationsPaged` method, and dispatches success or failure actions accordingly.
   *
   * ### Trigger
   * - `NotificationActions.getAllNotificationsPaged`
   *
   * ### Flow
   * 1. Triggered by the `getAllNotificationsPaged` action with `isLoggingIn` and `storeIds`.
   * 2. Uses selectors to retrieve:
   *    - current user
   *    - login progress state
   *    - last notification document ID for pagination
   *    - user login status
   *    - whether user is a Techo Admin
   * 3. If user is logged in and `storeIds` are provided:
   *    - Calls `getLatestNotificationsPaged` on the notification service.
   *    - Maps each document in the snapshot to an `INotification`, appending `notificationId` and `isNotificationRead`.
   *    - Determines the new `lastDocumentId` based on whether there are more documents.
   * 4. On success:
   *    - Dispatches `NotificationActions.getAllNotificationsPagedSuccess`
   *    - If `isLoggingIn` is true:
   *      - Dispatches `UserActions.setLoginProgress`
   *      - Dispatches `SettingsActions.getAllUserStoreSettings`
   * 5. On error:
   *    - If user is admin, logs the error to the console.
   *    - Dispatches `NotificationActions.setGlobalErrorOccurrence` with structured error metadata.
   *    - Dispatches fallback UI actions to reset notification state flags.
   *
   * ### Side Effects
   * - May update login progress and load user store settings during login.
   * - May log debug information to the console if user is a Techo Admin.
   *
   * ### Returns
   * - On success:
   *   - `NotificationActions.getAllNotificationsPagedSuccess`
   *   - `UserActions.setLoginProgress` (if logging in)
   *   - `SettingsActions.getAllUserStoreSettings` (if logging in)
   * - On failure:
   *   - `NotificationActions.setGlobalErrorOccurrence`
   *   - `NotificationActions.setPropertyOnNotificationState` (to reset loading flags)
   */

  getAllNotificationsPaged$ = createEffect(() => {
      return this.actions$.pipe(
        ofType(NotificationActions.getAllNotificationsPaged),
        withLatestFrom(
          this.store.select(selectUser),
          this.store.select(selectLoginProgressProperty),
          this.store.select(selectNotificationLastDocumentId),
          this.store.select(selectIsUserLoggedIn),
        ),
        filter(([_, user]) => !!user),
        mergeMap(([action, user, loginSteps, lastDocId, isLoggedIn]) => {
          const {isLoggingIn, storeIds} = action;

          if (!isLoggedIn || !storeIds?.length) {
            return EMPTY;
          }

          return from(
            this.notificationService.getLatestNotificationsPaged(
              path_notifications(),
              storeIds,
              lastDocId,
            ),
          ).pipe(
            map(({snapshot, hasMore}) => {
              const notifications = snapshot.docs.map(doc => {
                const data = doc.data();
                return {
                  ...data,
                  notificationId: doc.id,
                  isNotificationRead: data.hasOwnProperty('isNotificationRead') ? data.isNotificationRead : false,
                } as INotification;
              });

              const newLastDocId = hasMore ? snapshot.docs.at(-1)?.id ?? undefined : null;

              return NotificationActions.getAllNotificationsPagedSuccess({
                isLoggingIn,
                notifications,
                storeIds,
                lastDocumentId: newLastDocId,
              });
            }),
            mergeMap((successAction) => {
              const actions: Action[] = [successAction];

              if (isLoggingIn === true) {
                actions.push(
                  UserActions.setLoginProgress({
                    progress: {
                      ...loginSteps,
                      currentStep: 5,
                      percentComplete: calculatePercentile(5, loginSteps.totalSteps),
                    },
                  }),
                  SettingsActions.getAllUserStoreSettings(),
                );
              }

              return actions;
            }),
            catchError((error) => {
              return of(
                NotificationActions.setGlobalErrorOccurrence({
                  error: {
                    ...error,
                    action: JSON.stringify(action),
                    actionType: action.type,
                    originalError: error,
                    userId: user.id,
                    storeIds,
                  },
                }),
                setPropertyOnNotificationState({
                  property: 'isUserNotificationsLoading',
                  value: false,
                }),
                setPropertyOnNotificationState({
                  property: 'isUserScrollLoading',
                  value: false,
                }),
              );
            }),
          );
        }),
      );
    },
  );
  //#endregion

  //#region GET ALL UNREAD NOTIFICATIONS
  /**
   * ### getAllUnreadNotifications$
   * Effect to handle loading all unread notifications from Firestore.
   *
   * This effect listens for the `NotificationActions.getAllUnreadNotifications` action,
   * calls the notification service to fetch unread notifications,
   * and dispatches success or failure actions accordingly.
   *
   * ### Trigger
   * - `NotificationActions.getAllUnreadNotifications`
   *
   * ### Flow
   * 1. Triggered by the `getAllUnreadNotifications` action with optional `storeIds`.
   * 2. Uses selectors to retrieve:
   *    - current user
   *    - user login status
   *    - whether user is a Techo Admin
   * 3. If the user is logged in and `storeIds` are provided:
   *    - Calls `notificationService.getUnreadNotifications$` to fetch unread notifications.
   * 4. On success:
   *    - Dispatches `NotificationActions.getAllUnreadNotificationsSuccessful` with the unread notifications.
   * 5. On error:
   *    - If user is admin, logs the error to the console.
   *    - Dispatches `NotificationActions.setGlobalErrorOccurrence` with detailed error info.
   *    - Dispatches actions to reset loading state flags (`isUserNotificationsLoading`, `isUserScrollLoading`).
   *
   * ### Side Effects
   * - May log error details for Techo Admin users.
   *
   * ### Returns
   * - On success:
   *   - `NotificationActions.getAllUnreadNotificationsSuccessful`
   * - On failure:
   *   - `NotificationActions.setGlobalErrorOccurrence`
   *   - `NotificationActions.setPropertyOnNotificationState` (to reset loading flags)
   */

  getAllUnreadNotifications$ = createEffect(() => {
      return this.actions$.pipe(
        ofType(NotificationActions.getAllUnreadNotifications),
        withLatestFrom(
          this.store.select(selectUser),
          this.store.select(selectIsUserLoggedIn),
        ),
        filter(([_, user]) => !!user),
        mergeMap(([action, user, isLoggedIn]) => {
          const {storeIds} = action;

          if (!isLoggedIn || !storeIds?.length) {
            return EMPTY;
          }

          return from(
            this.notificationService.getUnreadNotifications$(
              path_notifications(),
              storeIds,
            ),
          ).pipe(
            map((unreadNotifications: INotification[]) => {
              return NotificationActions.getAllUnreadNotificationsSuccessful({
                storeIds,
                unreadNotifications,
              });
            }),
            catchError((error) => {
              return of(
                NotificationActions.setGlobalErrorOccurrence({
                  error: {
                    ...error,
                    action: JSON.stringify(action),
                    actionType: action.type,
                    originalError: error,
                    userId: user.id,
                    storeIds,
                  },
                }),
                setPropertyOnNotificationState({
                  property: 'isUserNotificationsLoading',
                  value: false,
                }),
                setPropertyOnNotificationState({
                  property: 'isUserScrollLoading',
                  value: false,
                }),
              );
            }),
          );
        }),
      );
    },
  );
  //#endregion

  //#region GET ALL UNSUCCESSFULLY UNREAD NOTIFICATIONS
  /**
   * ### getAllUnsuccessfulUnreadNotifications$
   * Effect to handle loading all unsuccessful unread notifications from Firestore.
   *
   * This effect listens for the `NotificationActions.getAllUnsuccessfulUnreadNotifications` action,
   * calls the notification service to fetch unread notifications (unsuccessful ones),
   * and dispatches success or failure actions accordingly.
   *
   * ### Trigger
   * - `NotificationActions.getAllUnsuccessfulUnreadNotifications`
   *
   * ### Flow
   * 1. Triggered by the `getAllUnsuccessfulUnreadNotifications` action with optional `storeIds`.
   * 2. Uses selectors to retrieve:
   *    - current user
   *    - user login status
   *    - whether user is a Techo Admin
   * 3. If the user is logged in and `storeIds` are provided:
   *    - Calls `notificationService.getUnreadNotifications$` to fetch unread notifications.
   * 4. On success:
   *    - Dispatches `NotificationActions.getAllUnsuccessfulUnreadNotificationsSuccessful` with the unread notifications.
   * 5. On error:
   *    - If user is admin, logs the error to the console.
   *    - Dispatches `NotificationActions.setGlobalErrorOccurrence` with detailed error info.
   *    - Dispatches actions to reset loading state flags (`isUserNotificationsLoading`, `isUserScrollLoading`).
   *
   * ### Side Effects
   * - May log error details for Techo Admin users.
   *
   * ### Returns
   * - On success:
   *   - `NotificationActions.getAllUnsuccessfulUnreadNotificationsSuccessful`
   * - On failure:
   *   - `NotificationActions.setGlobalErrorOccurrence`
   *   - `NotificationActions.setPropertyOnNotificationState` (to reset loading flags)
   */

  getAllUnsuccessfulUnreadNotifications$ = createEffect(() => {
      return this.actions$.pipe(
        ofType(NotificationActions.getAllUnsuccessfulUnreadNotifications),
        withLatestFrom(
          this.store.select(selectUser),
          this.store.select(selectIsUserLoggedIn),
        ),
        filter(([_, user]) => !!user),
        mergeMap(([action, user, isLoggedIn]) => {
          const {storeIds} = action;

          if (!isLoggedIn || !storeIds?.length) {
            return EMPTY;
          }

          return from(
            this.notificationService.getUnreadNotifications$(
              path_notifications(),
              storeIds,
            ),
          ).pipe(
            map((unreadNotifications: INotification[]) => {
              return NotificationActions.getAllUnsuccessfulUnreadNotificationsSuccessful({
                storeIds,
                unreadNotifications,
              });
            }),
            catchError((error) => {
              return of(
                NotificationActions.setGlobalErrorOccurrence({
                  error: {
                    ...error,
                    action: JSON.stringify(action),
                    actionType: action.type,
                    originalError: error,
                    userId: user.id,
                    storeIds,
                  },
                }),
                setPropertyOnNotificationState({
                  property: 'isUserNotificationsLoading',
                  value: false,
                }),
                setPropertyOnNotificationState({
                  property: 'isUserScrollLoading',
                  value: false,
                }),
              );
            }),
          );
        }),
      );
    },
  );
  //#endregion

  //#region UPDATE NOTIFICATION
  /**
   * Effect to update a notification's read status.
   *
   * @description
   * This effect listens to the `updateNotification` action. It uses the notification service to update the `read` status
   * of the notification. Depending on the result, it dispatches either a success action indicating the update was successful
   * or an error action with details about the failure.
   *
   * ### Dispatches:
   * - `updateNotificationSuccess` on success
   * - `setGlobalErrorOccurrence` on failure
   * - `setPropertyOnNotificationState` to update loading state on failure
   *
   * @effect
   */
  updateNotification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationActions.updateNotification),
      mergeMap((action) =>
        this.notificationService.updateDocumentAsObservable<INotification>(
          path_notification_id(action.notification),
          {
            ...(action.notification.read !== undefined ? {read: action.notification.read} : {}),
            reverted: action.notification.reverted ?? false,
            retried: action.notification.retried ?? false,
            ...(action.notification.isNotificationRead !== undefined
              ? {isNotificationRead: action.notification.isNotificationRead}
              : {}),
          } as Partial<INotification>,
          true,
        ).pipe(
          map(() =>
            NotificationActions.updateNotificationSuccess(),
          ),
          catchError((error) =>
            of(
              NotificationActions.setGlobalErrorOccurrence({
                error: {
                  ...error,
                  action: JSON.stringify(action),
                  actionType: action.type,
                  originalError: error,
                  storeIds: [action.notification.storeId],
                  userId: action.notification.userId,
                },
              }),
              setPropertyOnNotificationState({
                property: 'isUpdateNotificationLoading',
                value: false,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  //#endregion

  //#region BATCH UPDATE NOTIFICATION
  /**
   * Effect to batch update notifications.
   *
   * This effect listens for the `batchUpdateNotifications` action, which contains a list of notifications to be updated.
   * It then calls `updateDocumentsAsObservable` from the notification service to batch update the notifications' 'read' status.
   * If the update is successful, it dispatches the `updateNotificationSuccess` action.
   * If an error occurs, it dispatches `setGlobalErrorOccurrence` with the error details and stops the loading state for notification updates.
   *
   * ### Dispatches:
   * - `updateNotificationSuccess` on successful batch update of notifications.
   * - `setGlobalErrorOccurrence` on failure, with the error details for further processing.
   * - `setPropertyOnNotificationState` to stop the loading state when there's an error.
   *
   * @effect
   * @property {Array<INotification>} notifications - The list of notifications to be updated.
   * @property {boolean} read - The 'read' status to be updated for each notification.
   * @property {string} storeId - The store ID related to the notifications.
   * @property {string} userId - The user ID associated with the notifications.
   *
   */
  batchUpdateNotification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationActions.batchUpdateNotifications),
      mergeMap((action) => {
        const updates = action.notifications.map(notification => ({
          path: path_notification_id(notification),
          data: {
            read: notification.read,
          },
        }));
        return this.notificationService.updateDocumentsAsObservable<INotification>(
          updates,
          action.merge,
        ).pipe(
          map(() => NotificationActions.updateNotificationSuccess()),
          catchError((error) =>
            of(
              NotificationActions.setGlobalErrorOccurrence({
                error: {
                  ...error,
                  action: JSON.stringify(action),
                  actionType: action.type,
                  originalError: error,
                  storeIds: [action.notifications[0].storeId],
                  userId: action.notifications[0].userId,
                },
              }),
              setPropertyOnNotificationState({
                property: 'isUpdateNotificationLoading',
                value: false,
              }),
            ),
          ),
        );
      }),
    ),
  );

  //#endregion

  //#region GET NOTIFICATION AUTO ORDER
  /**
   * Effect to fetch the auto-order details for a notification.
   *
   * @description
   * This effect listens to the `getNotificationAutoOrder` action. It combines the action with the list of suppliers for the
   * currently selected store. It then fetches the auto-order details for the provided store ID and event, using the notification
   * service. Depending on the result, it dispatches either a success action with the fetched auto-order and supplier details
   * or a failure action with the notification and error details.
   *
   * ### Dispatches:
   * - `getNotificationAutoOrderSuccess` on success with the auto-order and notification
   * - `getNotificationAutoOrderFailure` on failure with the notification
   * - `setGlobalErrorOccurrence` on failure with detailed error information
   *
   * @effect
   */
  getNotificationAutoOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationActions.getNotificationAutoOrder),
      withLatestFrom(
        this.store.pipe(select(selectSuppliersByUserSelectedStore)),
      ),
      mergeMap(([action, suppliers]) =>
        this.notificationService.getDocumentAsObservable<IAutoOrderOrder>(
          path_operational_stores_data_auto_ordering_auto_orders_order_id(
            action.notification.storeId,
            action.notification.event,
          ),
        ).pipe(
          map((autoOrder: IAutoOrderOrder | undefined) => {
            if (!autoOrder) {
              return NotificationActions.getNotificationAutoOrderFailure({
                notification: action.notification,
              });
            }
            const supplierName: string = autoOrder.supplierId
              ? suppliers[autoOrder.supplierId]?.name ?? ''
              : '';
            return NotificationActions.getNotificationAutoOrderSuccess({
              autoOrder: {
                ...autoOrder,
                orderId: autoOrder.orderId ?? action.notification.event,
                supplierName,
              },
              notification: action.notification,
            });
          }),
          catchError((error) =>
            of(
              NotificationActions.getNotificationAutoOrderFailure({
                notification: action.notification,
              }),
              NotificationActions.setGlobalErrorOccurrence({
                error: {
                  ...error,
                  action: JSON.stringify(action),
                  actionType: action.type,
                  originalError: error,
                  storeIds: [action.notification.storeId],
                  userId: action.notification.userId,
                },
              }),
            ),
          ),
        ),
      ),
    ),
  );

  //#endregion

  //#region GET NOTIFICATION STOCK UPDATE
  /**
   * Effect to fetch the stock update details for a notification.
   *
   * @description
   * This effect listens to the `getNotificationStockUpdate` action. It takes the action's notification payload
   * and immediately dispatches the `getNotificationStockUpdateSuccess` action with the same notification. This effect
   * does not perform any service calls, it is a simple action-to-action mapping effect.
   *
   * ### Dispatches:
   * - `getNotificationStockUpdateSuccess` with the notification
   *
   * @effect
   */
  getNotificationStockUpdate$t = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationActions.getNotificationStockUpdate),
      map(({notification}) => NotificationActions.getNotificationStockUpdateSuccess({notification})),
    ),
  );

  /**
   * Effect to fetch the stock update details for a notification.
   *
   * @description
   * This effect listens to the `getNotificationStockUpdate` action. It takes the action's notification payload
   * and immediately dispatches the `getNotificationStockUpdateSuccess` action with the same notification. This effect
   * does not perform any service calls, it is a simple action-to-action mapping effect.
   *
   * ### Dispatches:
   * - `getNotificationStockUpdateSuccess` with the notification
   *
   * @effect
   */
  getStockUpdateChanges$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getStockUpdateChanges),
      withLatestFrom(
        this.store.pipe(select(selectUser)),
      ),
      switchMap(([action, user]) =>
        from(
          this.notificationService.getDocumentAsPromise(
            path_operational_stores_data_storeId_data_api_events_eventId(action.storeId, action.eventId),
          ),
        ).pipe(
          map((doc: ApiLogObj2) => getStockUpdateChangesSuccess({
            stockViewChanges: mergeTagChangesIntoStockViewChanges(
              doc.data,
              doc.taggedItems,
            ),
          })),
          catchError((error) => {
              const isPermDenied =
                (error as FirebaseError)?.code === 'permission-denied' ||
                (error)?.message?.includes(
                  'Missing or insufficient permissions',
                );

              if (isPermDenied) {
                const modalProps: ISharedModalBasic = {
                  buttonAccept: false,
                  buttonAcceptText: '',
                  buttonClose: true,
                  buttonCloseCross: false,
                  buttonCloseText: 'Cancel',
                  modalTitle: `Permission Denied`,
                  modalTitleIcon: IconsEnum.bugOutline,
                  modalTitleIconColor: IconColourEnum.red,
                  contextHeading: 'You do not have permission to view these changes.',
                  contextDescription: ['Please contact your admin if you need to update your permissions'],
                  cssClass: ['shared-basic-modal-css-small'],
                };
                this.ngZone.run(() => {
                  void this.sharedModalUtils.createModal(modalProps);
                });
              }

              return from([NotificationActions.setGlobalErrorOccurrence({
                error: {
                  name: 'ViewChangesError',
                  action: JSON.stringify(action),
                  actionType: action.type,
                  message: 'Error getting stock update changes',
                  originalError: error,
                  storeIds: [action.storeId],
                  userId: user.id,
                },
              }), NotificationActions.setPropertyOnNotificationState({
                property: 'isGetStockUpdateChangesLoading',
                value: false,
              }), UserActions.setGlobalModalVisibility({
                visibility: true, modalType: [ModalType.Notifications],
              })]);
            },
          ),
        ),
      ),
    ),
  );

  /**
   * Effect to fetch the stock update details for a notification.
   *
   * @description
   * This effect listens to the `getNotificationStockUpdate` action. It takes the action's notification payload
   * and immediately dispatches the `getNotificationStockUpdateSuccess` action with the same notification. This effect
   * does not perform any service calls, it is a simple action-to-action mapping effect.
   *
   * ### Dispatches:
   * - `getNotificationStockUpdateSuccess` with the notification
   *
   * @effect
   */
  revertStockUpdate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationActions.revertStockUpdate),
      withLatestFrom(
        this.store.pipe(select(selectUser)),
        this.store.pipe(select(selectSelectedNotification)),
      ),
      switchMap(([action, user, notification]) => {
        const tagRestores: Record<string, string[]> = extractStockTagRestorePayload(action.stockViewChanges);
        const store: IStore = {storeId: notification.storeId, name: notification.storeId};

        return from(this.firebaseService.stockUpdate(notification.storeId, action.stockViewChanges, false)).pipe(
          switchMap(() => {
            if (Object.keys(tagRestores).length === 0) {
              return of(undefined);
            }
            return from(this.collectionSharedStockService.updateStockTags(tagRestores, store));
          }),
          concatMap((): Action[] => {
            const updatedNotification = {
              ...notification,
              reverted: true,
            };
            return [
              NotificationActions.revertStockUpdateSuccess(),
              NotificationActions.updateNotification({notification: updatedNotification}),
              setGlobalModalVisibility({
                visibility: true,
                modalType: [ModalType.Notifications],
              }),
            ];
          }),
          catchError((error: Error) =>
            of(
              NotificationActions.setGlobalErrorOccurrence({
                error: {
                  ...error,
                  action: JSON.stringify(action),
                  actionType: action.type,
                  originalError: error,
                  storeIds: [notification.storeId],
                  userId: user.id,
                },
              }),
            ),
          ),
        );
      }),
    ),
  );

  retryStockUpdate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationActions.retryStockUpdate),
      withLatestFrom(
        this.store.pipe(select(selectUser)),
        this.store.pipe(select(selectSelectedNotification)),
      ),
      switchMap(([action, user, notification]) =>
        from(this.firebaseService.stockUpdate(
          notification.storeId,
          action.stockViewChanges,
          false,
          notification.newStockItem ?? false,
        )).pipe(
          concatMap((): Action[] => {
            const markRetriedActions: Action[] = notification.retried
              ? []
              : [NotificationActions.updateNotification({
                notification: {
                  ...notification,
                  retried: true,
                },
              })];
            return [
              ...markRetriedActions,
              NotificationActions.retryStockUpdateSuccess(),
              NotificationActions.setRetryStockUpdate({retryStockUpdate: false, excludeFailedBarcodes: false}),
              NotificationActions.setPropertyOnNotificationState({property: 'stockViewChanges', value: {}}),
              setGlobalModalVisibility({
                visibility: false,
                modalType: [],
              }),
            ];
          }),
          catchError((error: Error) =>
            of(
              NotificationActions.setGlobalErrorOccurrence({
                error: {
                  ...error,
                  action: JSON.stringify(action),
                  actionType: action.type,
                  originalError: error,
                  storeIds: [notification.storeId],
                  userId: user.id,
                },
              }),
              NotificationActions.setPropertyOnNotificationState({
                property: 'isGetStockUpdateChangesLoading',
                value: false,
              }),
            ),
          ),
        ),
      ),
    ),
  );


  //#endregion

  constructor(
    private sharedModalUtils: SharedModalUtils,
    private notificationService: NotificationService,
    private firebaseService: FirebaseService,
    private collectionSharedStockService: CollectionSharedStockService,
    private actions$: Actions,
    private ngZone: NgZone,
    private readonly store: Store,
  ) {
  }
}
